import React, { useCallback, useEffect, useState } from "react"
import {
    Alert,
    Box,
    Button,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    IconButton,
    Snackbar,
    Switch,
    TextField,
    Tooltip,
    Typography,
} from "@mui/material"
import SaveIcon from "@mui/icons-material/Save"
import PercentIcon from "@mui/icons-material/Percent"
import AddIcon from "@mui/icons-material/Add"
import DeleteIcon from "@mui/icons-material/DeleteOutline"
import { useTranslation } from "react-i18next"
import {
    getOutletVatRates,
    updateOutletVatRates,
    createVatRate,
    deleteVatRate,
} from "../../services/outletSettingsService"

interface VatRatesSectionProps {
    companyId: string
    outletId: string
}

interface VatRateRow {
    id: string
    label: string
    percentage: number
    is_active: boolean
    is_enabled: boolean
    display_order: number
}

const VatRatesSection: React.FC<VatRatesSectionProps> = ({ companyId, outletId }) => {
    const { t } = useTranslation()
    const [rates, setRates] = useState<VatRateRow[]>([])
    const [loading, setLoading] = useState(true)
    const [saving, setSaving] = useState(false)
    const [creating, setCreating] = useState(false)
    const [deletingId, setDeletingId] = useState<string | null>(null)
    const [confirmDelete, setConfirmDelete] = useState<VatRateRow | null>(null)
    const [dirty, setDirty] = useState(false)
    const [showCreateDialog, setShowCreateDialog] = useState(false)
    const [createForm, setCreateForm] = useState({ label: "", percentage: "" })
    const [snackbar, setSnackbar] = useState<{
        open: boolean
        message: string
        severity: "success" | "error"
    }>({ open: false, message: "", severity: "success" })

    const loadRates = useCallback(async () => {
        if (!companyId || !outletId) return
        setLoading(true)
        try {
            const rows = await getOutletVatRates(companyId, outletId)
            setRates(rows || [])
            setDirty(false)
        } catch {
            setSnackbar({
                open: true,
                message: t("settings.vatRates.toast.loadError"),
                severity: "error",
            })
        } finally {
            setLoading(false)
        }
    }, [companyId, outletId])

    useEffect(() => {
        loadRates()
    }, [loadRates])

    const handleToggle = (rateId: string) => {
        setRates((prev) =>
            prev.map((rate) =>
                rate.id === rateId ? { ...rate, is_enabled: !rate.is_enabled } : rate
            )
        )
        setDirty(true)
    }

    const handleSave = async () => {
        setSaving(true)
        try {
            const payload = rates.map((rate, idx) => ({
                vat_rate_id: rate.id,
                is_enabled: !!rate.is_enabled,
                display_order: idx,
            }))
            await updateOutletVatRates(companyId, outletId, payload)
            await loadRates()
            setSnackbar({
                open: true,
                message: t("settings.vatRates.toast.saved"),
                severity: "success",
            })
        } catch {
            setSnackbar({
                open: true,
                message: t("settings.vatRates.toast.saveError"),
                severity: "error",
            })
        } finally {
            setSaving(false)
        }
    }

    const handleOpenCreateDialog = () => {
        setCreateForm({ label: "", percentage: "" })
        setShowCreateDialog(true)
    }

    const handleCreate = async () => {
        const label = createForm.label.trim()
        const percentage = Number(createForm.percentage)
        if (!label) {
            setSnackbar({
                open: true,
                message: t("settings.vatRates.toast.labelRequired"),
                severity: "error",
            })
            return
        }
        if (!Number.isFinite(percentage) || percentage < 0) {
            setSnackbar({
                open: true,
                message: t("settings.vatRates.toast.percentageInvalid"),
                severity: "error",
            })
            return
        }
        setCreating(true)
        try {
            await createVatRate(companyId, { label, percentage, is_active: true })
            setShowCreateDialog(false)
            await loadRates()
            setSnackbar({
                open: true,
                message: t("settings.vatRates.toast.created"),
                severity: "success",
            })
        } catch (e: any) {
            setSnackbar({
                open: true,
                message: e?.message || t("settings.vatRates.toast.createError"),
                severity: "error",
            })
        } finally {
            setCreating(false)
        }
    }

    const handleConfirmDelete = async () => {
        if (!confirmDelete) return
        const rateId = confirmDelete.id
        setDeletingId(rateId)
        try {
            await deleteVatRate(companyId, rateId)
            setConfirmDelete(null)
            await loadRates()
            setSnackbar({
                open: true,
                message: t("settings.vatRates.toast.deleted"),
                severity: "success",
            })
        } catch (e: any) {
            setSnackbar({
                open: true,
                message: e?.message || t("settings.vatRates.toast.deleteError"),
                severity: "error",
            })
        } finally {
            setDeletingId(null)
        }
    }

    if (loading) {
        return (
            <>
                <Typography
                    variant="h6"
                    fontWeight={600}
                    sx={{ display: "flex", alignItems: "center", gap: 1, mb: 2 }}
                >
                    <PercentIcon sx={{ color: "#6366f1" }} />
                    {t("settings.vatRates.title")}
                </Typography>
                <Box sx={{ display: "flex", justifyContent: "center", py: 3 }}>
                    <CircularProgress size={24} />
                </Box>
            </>
        )
    }

    return (
        <>
            <Box
                sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    mb: 2,
                }}
            >
                <Typography
                    variant="h6"
                    fontWeight={600}
                    sx={{ display: "flex", alignItems: "center", gap: 1 }}
                >
                    <PercentIcon sx={{ color: "#6366f1" }} />
                    {t("settings.vatRates.title")}
                </Typography>
                <Box sx={{ display: "flex", gap: 1 }}>
                    <Button
                        variant="outlined"
                        size="small"
                        startIcon={<AddIcon />}
                        onClick={handleOpenCreateDialog}
                        sx={{ textTransform: "none" }}
                    >
                        {t("settings.vatRates.add")}
                    </Button>
                    <Button
                        variant="contained"
                        size="small"
                        startIcon={saving ? <CircularProgress size={16} /> : <SaveIcon />}
                        disabled={!dirty || saving}
                        onClick={handleSave}
                        sx={{
                            textTransform: "none",
                            bgcolor: "#6366f1",
                            "&:hover": { bgcolor: "#4f46e5" },
                        }}
                    >
                        {t("settings.vatRates.save")}
                    </Button>
                </Box>
            </Box>

            {rates.length === 0 ? (
                <Alert severity="info">{t("settings.vatRates.empty")}</Alert>
            ) : (
                <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
                    {rates.map((rate) => (
                        <Box
                            key={rate.id}
                            sx={{
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "space-between",
                                py: 1,
                                px: 1.5,
                                borderRadius: 1,
                                bgcolor: rate.is_enabled ? "#f0fdf4" : "#fafafa",
                                border: "1px solid",
                                borderColor: rate.is_enabled ? "#bbf7d0" : "#e5e7eb",
                            }}
                        >
                            <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                                <Box
                                    sx={{
                                        width: 30,
                                        height: 30,
                                        borderRadius: "8px",
                                        display: "flex",
                                        alignItems: "center",
                                        justifyContent: "center",
                                        bgcolor: "#eef2ff",
                                        color: "#374151",
                                        fontWeight: 700,
                                        fontSize: "0.78rem",
                                    }}
                                >
                                    %
                                </Box>
                                <Box>
                                    <Typography sx={{ fontWeight: 500, fontSize: "0.95rem" }}>
                                        {rate.label}
                                    </Typography>
                                    <Typography sx={{ color: "#6b7280", fontSize: "0.78rem" }}>
                                        {Number(rate.percentage || 0).toFixed(2)}%
                                    </Typography>
                                </Box>
                            </Box>
                            <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                                <Switch
                                    checked={rate.is_enabled}
                                    onChange={() => handleToggle(rate.id)}
                                    disabled={saving || !rate.is_active}
                                    color="success"
                                />
                                <Tooltip title={t("settings.vatRates.delete") as string}>
                                    <span>
                                        <IconButton
                                            size="small"
                                            onClick={() => setConfirmDelete(rate)}
                                            disabled={deletingId === rate.id || saving}
                                            sx={{ color: "#ef4444" }}
                                        >
                                            {deletingId === rate.id ? (
                                                <CircularProgress size={16} />
                                            ) : (
                                                <DeleteIcon fontSize="small" />
                                            )}
                                        </IconButton>
                                    </span>
                                </Tooltip>
                            </Box>
                        </Box>
                    ))}
                </Box>
            )}

            <Dialog
                open={showCreateDialog}
                onClose={() => !creating && setShowCreateDialog(false)}
                maxWidth="xs"
                fullWidth
            >
                <DialogTitle>{t("settings.vatRates.dialog.createTitle")}</DialogTitle>
                <DialogContent sx={{ pt: 1 }}>
                    <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
                        <TextField
                            label={t("settings.vatRates.fields.label") as string}
                            fullWidth
                            value={createForm.label}
                            onChange={(e) =>
                                setCreateForm((prev) => ({ ...prev, label: e.target.value }))
                            }
                            disabled={creating}
                            autoFocus
                        />
                        <TextField
                            label={t("settings.vatRates.fields.percentage") as string}
                            fullWidth
                            type="number"
                            inputProps={{ step: "0.01", min: 0 }}
                            value={createForm.percentage}
                            onChange={(e) =>
                                setCreateForm((prev) => ({
                                    ...prev,
                                    percentage: e.target.value,
                                }))
                            }
                            disabled={creating}
                        />
                    </Box>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setShowCreateDialog(false)} disabled={creating}>
                        {t("settings.vatRates.dialog.cancel")}
                    </Button>
                    <Button
                        variant="contained"
                        onClick={handleCreate}
                        disabled={creating}
                        startIcon={creating ? <CircularProgress size={14} /> : <AddIcon />}
                    >
                        {t("settings.vatRates.dialog.create")}
                    </Button>
                </DialogActions>
            </Dialog>

            <Dialog
                open={!!confirmDelete}
                onClose={() => deletingId === null && setConfirmDelete(null)}
                maxWidth="xs"
                fullWidth
            >
                <DialogTitle>{t("settings.vatRates.dialog.deleteTitle")}</DialogTitle>
                <DialogContent>
                    <Typography sx={{ fontSize: "0.95rem" }}>
                        {t("settings.vatRates.dialog.deleteMessage", {
                            label: confirmDelete?.label || "",
                        })}
                    </Typography>
                </DialogContent>
                <DialogActions>
                    <Button
                        onClick={() => setConfirmDelete(null)}
                        disabled={deletingId !== null}
                    >
                        {t("settings.vatRates.dialog.cancel")}
                    </Button>
                    <Button
                        variant="contained"
                        color="error"
                        onClick={handleConfirmDelete}
                        disabled={deletingId !== null}
                        startIcon={
                            deletingId !== null ? (
                                <CircularProgress size={14} />
                            ) : (
                                <DeleteIcon />
                            )
                        }
                    >
                        {t("settings.vatRates.dialog.confirm")}
                    </Button>
                </DialogActions>
            </Dialog>

            <Snackbar
                open={snackbar.open}
                autoHideDuration={3000}
                onClose={() => setSnackbar((state) => ({ ...state, open: false }))}
            >
                <Alert
                    severity={snackbar.severity}
                    onClose={() => setSnackbar((state) => ({ ...state, open: false }))}
                >
                    {snackbar.message}
                </Alert>
            </Snackbar>
        </>
    )
}

export default VatRatesSection
